import { maxBy, sumBy } from "lodash"
import { useMemo } from "react"
import { useSessionStorage } from "usehooks-ts"
import { NOOS_SUPPLY_PLAN_TRIGGERS_TABLE_ID } from "~/constants"
import { useNoosSupplyPlanDetailContext } from "~/context/NoosSupplyPlanDetailProvider"
import { useNoosPlanVariantIds } from "./useNoosPlanVariantIds"
import { parseSettings } from "~/hooks/useSupplyPlan"
import { useVariantsByIds } from "~/hooks/useVariants"
import { useAllStockOutTimes } from "~/hooks/useVariantTableHooks"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { api, type RouterOutputs } from "~/utils/api"
import { startOfTodayUTC } from "~/utils/datetime"
import { scopeVariantToWarehouse } from "./scopeVariantToWarehouse"
import {
  computeVariantMoqQty,
  type SkuSuggestedOrder,
  type SkuSuggestedOrderMap,
} from "./triggerVariantMath"
import {
  getTriggerReasons,
  getTriggerTiming,
  TRIGGER_REASONS,
  type TriggerReason,
  type TriggerTiming,
} from "./triggerReasons"
import { useNoosBuySuggestion } from "./useNoosBuySuggestion"

export type DelayedPoWarning =
  RouterOutputs["stockOutTimes"]["getDelayedPoSuggestionWarnings"][number]

type SuggestedOrderLine =
  RouterOutputs["stockOutTimes"]["getClosingStockAndSuggestedOrders"]["suggestedOrders"][number]

export type TriggerViewMode = "suggested" | "snoozed" | "all"

export type VariantTrigger = { timing: TriggerTiming; reasons: TriggerReason[] }

// The headline PO is the latest across all warehouses; scoped to one, it must be that warehouse's.
function scopeDelayedPoWarning(
  warning: DelayedPoWarning,
  warehouseId: string | undefined,
): DelayedPoWarning | undefined {
  if (!warehouseId) return warning
  const pos = warning.allPos.filter((po) => po.warehouseId === warehouseId)
  const latest = maxBy(pos, (po) => new Date(po.poExpectedArrivalDate).getTime())
  if (!latest) return undefined
  return {
    ...warning,
    poName: latest.poName,
    poExpectedArrivalDate: latest.poExpectedArrivalDate,
    allPos: pos,
  }
}

export function sumSuggestedOrdersBySku(
  suggestedOrders: Pick<SuggestedOrderLine, "skuId" | "quantity" | "moqQuantity">[],
): SkuSuggestedOrderMap {
  const map = new Map<string, SkuSuggestedOrder>()
  for (const o of suggestedOrders) {
    const moq = o.moqQuantity ?? o.quantity
    const existing = map.get(o.skuId)
    map.set(o.skuId, {
      quantity: (existing?.quantity ?? 0) + o.quantity,
      moqQuantity: (existing?.moqQuantity ?? 0) + moq,
    })
  }
  return map
}

/** The rows each default Triggers view shows. Shared with the Overview summary so counts match. */
export function variantsForTriggerView(
  viewMode: TriggerViewMode,
  allVariants: IVariantSummary[],
  snoozedUntilMap: Map<string, Date>,
  skuSuggestedOrderData: SkuSuggestedOrderMap,
): IVariantSummary[] {
  if (viewMode === "snoozed") return allVariants.filter((v) => snoozedUntilMap.has(v.variantId))
  if (viewMode === "all") return allVariants
  return allVariants.filter(
    (v) =>
      !snoozedUntilMap.has(v.variantId) &&
      v.skuIds.some((skuId) => (skuSuggestedOrderData.get(skuId)?.quantity ?? 0) > 0),
  )
}

export type TriggerOverview = {
  suggested: IVariantSummary[]
  countsByReason: Record<TriggerReason, number>
  totalSuggestedQty: number
  total: number
}

/** The Overview's figures: the "Suggested variants" rows, their reasons and summed MOQ quantity. */
export function summarizeTriggerOverview(
  allVariants: IVariantSummary[],
  snoozedUntilMap: Map<string, Date>,
  skuSuggestedOrderData: SkuSuggestedOrderMap,
  triggerByVariant: Map<string, VariantTrigger>,
): TriggerOverview {
  const suggested = variantsForTriggerView(
    "suggested",
    allVariants,
    snoozedUntilMap,
    skuSuggestedOrderData,
  )
  const countsByReason = Object.fromEntries(TRIGGER_REASONS.map((r) => [r.id, 0])) as Record<
    TriggerReason,
    number
  >
  for (const v of suggested) {
    for (const reason of triggerByVariant.get(v.variantId)?.reasons ?? []) {
      countsByReason[reason] += 1
    }
  }
  return {
    suggested,
    countsByReason,
    totalSuggestedQty: sumBy(suggested, (v) => computeVariantMoqQty(v, skuSuggestedOrderData)),
    total: allVariants.length,
  }
}

/** The Triggers warehouse filter. Per plan, like the Availability tab's: another plan may not
 * stock the warehouse picked here. */
export function useTriggersWarehouseId() {
  const { supplyPlan } = useNoosSupplyPlanDetailContext()
  return useSessionStorage<string | undefined>(
    `${NOOS_SUPPLY_PLAN_TRIGGERS_TABLE_ID}-warehouseId-${supplyPlan.id}`,
    undefined,
  )
}

/** Everything the Triggers table derives its rows, reasons and timings from; all warehouses when
 * `warehouseId` is undefined. */
export function useNoosTriggers(warehouseId: string | undefined) {
  const { supplyPlan } = useNoosSupplyPlanDetailContext()

  const { variantIds, isLoading: isLoadingCollections } = useNoosPlanVariantIds()

  const storeVariants = useVariantsByIds(variantIds)
  const { data: stockOutTimes } = useAllStockOutTimes()
  const allVariants = useMemo(
    () =>
      storeVariants.map((v) =>
        scopeVariantToWarehouse(v, warehouseId, stockOutTimes?.leadTimeAvailability),
      ),
    [storeVariants, warehouseId, stockOutTimes],
  )

  const purchaseInterval = parseSettings(supplyPlan.settings).purchaseInterval

  const {
    query: { data: dynamicOrderData, isFetching },
  } = useNoosBuySuggestion({
    variantIds,
    buyDate: startOfTodayUTC(),
    isScoped: true,
    warehouseId,
  })

  const skuSuggestedOrderData = useMemo(
    () => sumSuggestedOrdersBySku(dynamicOrderData?.suggestedOrders ?? []),
    [dynamicOrderData],
  )

  const { data: delayedPoWarnings } = api.stockOutTimes.getDelayedPoSuggestionWarnings.useQuery(
    { variantIds },
    { enabled: variantIds.length > 0, staleTime: 0 },
  )
  const delayedPoWarningMap = useMemo(() => {
    const map = new Map<string, DelayedPoWarning>()
    for (const w of delayedPoWarnings ?? []) {
      const scoped = scopeDelayedPoWarning(w, warehouseId)
      if (scoped) map.set(w.variantId, scoped)
    }
    return map
  }, [delayedPoWarnings, warehouseId])

  const { data: snoozedVariants, isLoading: isLoadingSnoozed } =
    api.variantMetadata.getSnoozedVariants.useQuery(
      { variantIds },
      { enabled: variantIds.length > 0, staleTime: 0 },
    )
  const snoozedUntilMap = useMemo(
    () => new Map((snoozedVariants ?? []).map((s) => [s.variantId, new Date(s.snoozedUntil)])),
    [snoozedVariants],
  )

  const triggerByVariant = useMemo(() => {
    const today = startOfTodayUTC()
    return new Map<string, VariantTrigger>(
      allVariants.map((v) => {
        const timing = getTriggerTiming(v, today)
        const reasons = getTriggerReasons(
          v,
          timing,
          delayedPoWarningMap.get(v.variantId),
          purchaseInterval,
        )
        return [v.variantId, { timing, reasons }]
      }),
    )
  }, [allVariants, delayedPoWarningMap, purchaseInterval])

  // The suggestions query is disabled until the plan's variants are known, so isFetching alone
  // reads as "done" (and every count as 0) while the collections still load. The variant store
  // needs no check: the app renders no page before it's filled, and a plan whose variants are all
  // hidden or phased out leaves it empty for good.
  const isLoading =
    isLoadingCollections || isLoadingSnoozed || (variantIds.length > 0 && !dynamicOrderData)

  return {
    allVariants,
    isFetching,
    isLoading,
    skuSuggestedOrderData,
    delayedPoWarningMap,
    snoozedUntilMap,
    triggerByVariant,
  }
}

export type NoosTriggerOverview = TriggerOverview & { isPending: boolean }

/** The Overview's trigger figures, across all warehouses: the Triggers filter stays on that tab. */
export function useNoosTriggerOverview(): NoosTriggerOverview {
  const {
    allVariants,
    snoozedUntilMap,
    skuSuggestedOrderData,
    triggerByVariant,
    isFetching,
    isLoading,
  } = useNoosTriggers(undefined)
  const summary = useMemo(
    () =>
      summarizeTriggerOverview(
        allVariants,
        snoozedUntilMap,
        skuSuggestedOrderData,
        triggerByVariant,
      ),
    [allVariants, snoozedUntilMap, skuSuggestedOrderData, triggerByVariant],
  )
  return { ...summary, isPending: isFetching || isLoading }
}
